/**
 * Browser push-notification permission card (Step 1) — shown until the
 * user grants browser permission. Includes iOS add-to-home-screen
 * instructions when push is unsupported.
 */

import React from 'react';
import type { PermissionStatus } from '../../../hooks/useFCMToken';
import type { BrowserInfo, BrowserInstructions } from '../utils/browserInfo';

interface BrowserPermissionSectionProps {
  permissionStatus: PermissionStatus;
  browserInfo: BrowserInfo;
  browserInstructions: BrowserInstructions;
  isRequestingPermission: boolean;
  onRequestPermission: () => void;
}

export const BrowserPermissionSection: React.FC<BrowserPermissionSectionProps> = ({
  permissionStatus,
  browserInfo,
  browserInstructions,
  isRequestingPermission,
  onRequestPermission,
}) => {
  return (
    <div className="bg-surface-muted rounded-lg p-4 border border-line">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-8 h-8 rounded-full flex items-center justify-center bg-hue-blue-soft text-hue-blue-deep">
          <i className="fa-solid fa-bell"></i>
        </div>
        <div>
          <span className="font-semibold text-ink">Enable Push Notifications</span>
          <p className="text-xs text-ink-muted">Allow browser notifications to get alerts on this device</p>
        </div>
      </div>

      {/* Permission Status and Button */}
      {permissionStatus === 'unsupported' ? (
        <div className="p-3 bg-warning-subtle text-warning-strong border border-warning-border rounded-md text-sm">
          {browserInfo.isIOS ? (
            <div className="space-y-3">
              <div className="flex items-start gap-2">
                <i className="fa-solid fa-mobile-screen mt-0.5 flex-shrink-0"></i>
                <div>
                  <span className="font-medium">iOS requires the app to be added to your home screen</span>
                  <p className="mt-1 text-xs opacity-90">
                    Web push notifications on iPhone/iPad require iOS 16.4 or later and the site must be installed as an app.
                  </p>
                </div>
              </div>
              <div className="bg-surface/60 rounded-md p-3 space-y-2">
                <p className="font-medium text-xs">How to add to home screen:</p>
                <ol className="text-xs space-y-1.5 ml-4 list-decimal">
                  <li>
                    Tap the <strong>Share</strong> button{' '}
                    <span className="inline-flex items-center justify-center w-5 h-5 bg-hue-blue-soft rounded text-hue-blue-deep text-[10px]">
                      <i className="fa-solid fa-arrow-up-from-bracket"></i>
                    </span>{' '}
                    at the bottom of Safari
                  </li>
                  <li>Scroll down and tap <strong>"Add to Home Screen"</strong></li>
                  <li>Tap <strong>"Add"</strong> in the top right corner</li>
                  <li>Open the app from your home screen</li>
                  <li>Return here to enable notifications</li>
                </ol>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2">
              <i className="fa-solid fa-exclamation-triangle mt-0.5"></i>
              <span>Push notifications are not supported in this browser.</span>
            </div>
          )}
        </div>
      ) : permissionStatus === 'denied' ? (
        <div className="p-3 bg-warning-subtle text-warning-strong border border-warning-border rounded-md text-sm">
          <div className="flex items-start gap-2">
            <i className="fa-solid fa-ban mt-0.5 flex-shrink-0"></i>
            <div>
              <span className="font-medium">Notifications are blocked for this site.</span>
              <p className="mt-1 text-xs opacity-90">{browserInstructions.ifBlocked}</p>
            </div>
          </div>
        </div>
      ) : (
        <button
          onClick={onRequestPermission}
          disabled={isRequestingPermission}
          className="w-full px-4 py-2.5 bg-action text-ink-inverse rounded-lg font-medium hover:bg-action-hover focus:outline-none focus:ring-2 focus:ring-focus focus:ring-offset-2 disabled:opacity-50 disabled:cursor-wait transition-colors flex items-center justify-center gap-2"
        >
          {isRequestingPermission ? (
            <>
              <i className="fa-solid fa-circle-notch fa-spin"></i>
              <span>Waiting for permission...</span>
            </>
          ) : (
            <>
              <i className="fa-solid fa-bell"></i>
              <span>Allow Browser Notifications</span>
            </>
          )}
        </button>
      )}
    </div>
  );
};
